import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { PageHeader } from "../components/pageheader";
import OurWork from "@/assets/images/our-work.png";
import { motion, useAnimation, useInView } from "framer-motion";
import { TestimonialBlock } from "../components/testimonial";
import { AnimatedImage } from "../components/animatedimage";
import Stefan from "@/assets/images/stefan-o.png";

/* Container */
const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Reusable service block (card) */
type ServiceBlockProps = {
  title: string;
  text: string;
  href: string;
  clients?: string[];
};

export const ServiceBlock: React.FC<ServiceBlockProps> = ({
  title,
  text,
  href,
  clients = [],
}) => {
  const controls = useAnimation();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.15,
    margin: "-80px 0px -80px 0px",
  });

  React.useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 220, damping: 22 },
      });
    }
  }, [inView, controls]);

  // Fallback: om IO missar (t.ex. Safari/toppen), animera in efter mount
  React.useEffect(() => {
    const t = setTimeout(() => {
      controls.start({ opacity: 1, y: 0, transition: { duration: 0.4 } });
    }, 800);
    return () => clearTimeout(t);
  }, [controls]);

  return (
    <Link
      to={href}
      aria-label={`Read more about ${title}`}
      className="block h-full"
    >
      <motion.div
        ref={ref}
        initial={{ opacity: 0.01, y: 16 }} // 0.01 undviker vissa safari-buggar
        animate={controls}
        whileHover={{ y: -4, scale: 1.01 }}
        className="group relative rounded-xl bg-white/5 p-8 sm:p-10 overflow-hidden h-full will-change-transform"
      >
        {/* Title */}
        <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
          {title}
        </h3>

        {/* Body */}
        <p className="text-white/70 text-lg leading-relaxed max-w-prose mb-6">
          {text}
        </p>

        {/* Client badges */}
        {clients.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {clients.map((client) => (
              <motion.span
                key={client}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{
                  once: true,
                  amount: 0.2,
                  margin: "-40px 0px -40px 0px",
                }}
                transition={{ duration: 0.2 }}
                className="text-xs font-medium bg-white/10 text-white/80 px-2.5 py-1 rounded-full backdrop-blur-sm border border-white/10"
              >
                {client}
              </motion.span>
            ))}
          </div>
        )}

        {/* Arrow */}
        <span className="absolute bottom-0 right-0 m-0 pointer-events-none">
          <span className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-[#FF893F] group-hover:bg-[#FF893F] transition-colors rounded-tl-2xl">
            <svg
              className="w-6 h-6 translate-x-0.5 group-hover:translate-x-1 transition-transform"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14" />
              <path d="M12 5l7 7-7 7" />
            </svg>
          </span>
        </span>
      </motion.div>
    </Link>
  );
};

export default function ServicesPage() {
  const { hash, pathname } = useLocation();

  // Scroll to top on route changes without hash
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0 });
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Smooth scroll to hash targets
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [hash]);
  // högre upp i komponenten
  React.useEffect(() => {
    const img = new Image();
    img.src = OurWork;
  }, []);

  return (
    <section className="py-14 text-white">
      <Container>
        <PageHeader
          eyebrow="Services"
          title="Where we make impact"
          text="Our focus areas, building, scaling, and supporting digital products and teams."
        />
        <AnimatedImage
          src={OurWork}
          alt="Our work"
          loading="eager" // byt till "lazy" om längre ner på sidan
          fetchPriority="high"
          aspectClassName="" // säkrar layouten
          wrapperClassName=""
          once={true}
          amount={0.35}
        />
        {/* Service blocks grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          <ServiceBlock
            title="Apps"
            text="Designing and building progressive web and native apps. In commerce and other industries, B2B and B2C - we build digital products that scale and perform."
            href="/services/apps"
            clients={[
              "Kjell & Company",
              "Fitness24Seven",
              "Thule",
              "Ålö",
              "Öresundbron",
              "Spar Kjøp",
            ]}
          />
          <ServiceBlock
            title="Platforms"
            text="Beyond Backstage - designing and building developer platforms, enabling organisations to build and ship faster and with more confidence."
            href="/services/platforms"
            clients={["IKEA", "Länsförsäkringar"]}
          />
          <ServiceBlock
            title="Startups"
            text="We join designing and building the product, as advisors or investors, depending on what moves the product forward."
            href="/services/startups"
            clients={["PinShirt", "ComFoo"]}
          />
          <ServiceBlock
            title="Advisory & Change"
            text="We help organisations turn strategy into action, creating alignment, clarity, and capability to drive digital success."
            href="/services/advisory"
            clients={["Scandinavian Cosmetic"]}
          />
        </div>

        {/* Testimonial */}
        <TestimonialBlock
          quote="Working with BoldBrave never feels like working with an external agency. They operate as an extension of our own team — accountable, proactive, and always focused on the right outcomes."
          author="Stefan Olsson"
          role="Digital Development Manager, Kjell & Company"
          avatar={Stefan}
        />
      </Container>
    </section>
  );
}
